import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  MessageSquare,
  Phone,
  BarChart3,
  Sliders,
  CheckCircle2,
  Layers,
  Save,
  RotateCcw,
  Check,
  Award,
  Users,
  ShieldCheck,
  HeartHandshake,
  Zap,
  Star,
  Headphones,
  TrendingUp,
} from 'lucide-react';
import { SiteSettings } from '../../types.ts';

export default function FrontendCustomizerManager() {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  // Floating Contact Widget State
  const [contactWidget, setContactWidget] = useState(
    settings?.contactWidget || {
      enabled: true,
      whatsappNumber: settings?.contact?.whatsappNumber || '6281234567890',
      phoneNumber: settings?.contact?.phone || '081234567890',
      whatsappLabel: 'Chat WhatsApp CS',
      phoneLabel: 'Telepon Langsung',
      statusBadgeText: '🟢 Online 24 Jam | Fast Response',
      defaultWaMessage:
        'Halo Tim Konsultan Nusantara, saya ingin bertanya dan konsultasi mengenai penawaran layanan & paket.',
      popupGreeting: 'Butuh proposal cepat atau info paket? Hubungi kami langsung!',
      showPhone: true,
      showWhatsapp: true,
    }
  );

  // Quick Stats State
  const [quickStats, setQuickStats] = useState(
    settings?.quickStats || {
      items: [
        {
          id: 'qs-1',
          icon: 'Users',
          value: '500+',
          label: 'Klien Korporasi & Instansi',
          desc: 'Dipercaya berbagai BUMN & instansi swasta terkemuka',
        },
        {
          id: 'qs-2',
          icon: 'Award',
          value: '99.8%',
          label: 'Tingkat Kepuasan Peserta',
          desc: 'Rating bintang 5 dari ratusan ulasan positif',
        },
        {
          id: 'qs-3',
          icon: 'ShieldCheck',
          value: '100%',
          label: 'Standar Keamanan Bersertifikat',
          desc: 'Fasilitator & instruktur berlisensi resmi BNSP',
        },
        {
          id: 'qs-4',
          icon: 'HeartHandshake',
          value: '10+ Thn',
          label: 'Pengalaman Teruji',
          desc: 'Ahli dalam event, web & ekosistem UMKM',
        },
      ],
    }
  );

  // Softselling CTA State
  const [softsellingCta, setSoftsellingCta] = useState(
    settings?.softsellingCta || {
      badge: 'Fast Response & Free Proposal 24 Jam',
      title: 'Rencanakan Proyek, Portofolio & Kolaborasi Bisnis Anda',
      subtitle:
        'Konsultasikan konsep kerja sama, kebutuhan layanan, atau solusi bisnis Anda bersama tim kami. Gratis tanpa biaya awal.',
      benefit1: 'Proposal & Penawaran Cepat',
      benefit2: 'Tim Profesional Berpengalaman',
      benefit3: 'Konsultasi 100% Bebas Biaya',
      waBtnText: 'Hubungi Admin WhatsApp',
      phoneBtnText: 'Telepon Kantor Langsung',
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);

  const handleSaveAll = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      const activeWa = contactWidget.whatsappNumber?.trim() || '';
      const activePhone = contactWidget.phoneNumber?.trim() || '';

      const updatedWidget = {
        ...contactWidget,
        whatsappNumber: activeWa,
        phoneNumber: activePhone,
      };

      const updatedContact = {
        ...settings.contact,
        whatsappNumber: activeWa || settings.contact.whatsappNumber,
        phone: activePhone || settings.contact.phone,
      };

      const updated: SiteSettings = {
        ...settings,
        contactWidget: updatedWidget,
        quickStats,
        softsellingCta,
        contact: updatedContact,
      };

      await updateSettingsOnServer(updated);
      const timeStr = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastSavedTime(timeStr);
      showToast('Seluruh teks dan konfigurasi widget berhasil disimpan ke database!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Gagal menyimpan perubahan', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStatItem = (index: number, key: string, val: string) => {
    const updated = [...quickStats.items];
    updated[index] = { ...updated[index], [key]: val };
    setQuickStats({ ...quickStats, items: updated });
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-200">
      {/* Top Header & Save Button */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold mb-2 border border-emerald-200">
            <Sliders className="w-3.5 h-3.5" />
            <span>Kustomisasi Teks Front-End 100% Fleksibel</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
            Kustomisasi Kata, Frasa & Tombol Front-End
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Edit seluruh kalimat, nomor kontak WhatsApp, tombol floating, kartu angka statistik, teks banner soft-selling, dan footer.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            id="save-frontend-customizer-btn"
            onClick={handleSaveAll}
            disabled={isSaving}
            className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition-all shrink-0 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Menyimpan ke SQLite...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Simpan Semua Perubahan</span>
              </>
            )}
          </button>

          {lastSavedTime && (
            <div
              id="frontend-customizer-top-saved-notice"
              className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-bold animate-in fade-in slide-in-from-top-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
              <span>Berhasil disimpan ke database</span>
              <span className="text-[10px] text-emerald-700/80 font-mono font-medium">({lastSavedTime})</span>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 1: Floating WhatsApp & Phone Contact Widget */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900">
                1. Tombol Melayang Pojok Kanan Bawah (WhatsApp & Telepon)
              </h3>
              <p className="text-xs text-slate-500">
                Tombol mencolok di pojok kanan bawah yang langsung menarik perhatian pengunjung untuk reservasi.
              </p>
            </div>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={contactWidget.enabled}
              onChange={(e) => setContactWidget({ ...contactWidget, enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Nomor WhatsApp CS (Format internasional tanpa +, cth: 6281234567890)
            </label>
            <input
              type="text"
              value={contactWidget.whatsappNumber || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, whatsappNumber: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="6281234567890"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Nomor Telepon Kantor / Hotline Langsung
            </label>
            <input
              type="text"
              value={contactWidget.phoneNumber || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, phoneNumber: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="0812-3456-7890"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol WhatsApp
            </label>
            <input
              type="text"
              value={contactWidget.whatsappLabel || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, whatsappLabel: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Chat WhatsApp CS"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Label Tombol Telepon
            </label>
            <input
              type="text"
              value={contactWidget.phoneLabel || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, phoneLabel: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Telepon Langsung"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Teks Status Bubble / Badge Online
            </label>
            <input
              type="text"
              value={contactWidget.statusBadgeText || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, statusBadgeText: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="🟢 Online 24 Jam | Fast Response"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Pesan Popup Sapaan (Greeting)
            </label>
            <input
              type="text"
              value={contactWidget.popupGreeting || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, popupGreeting: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Butuh proposal cepat atau info paket? Hubungi kami langsung!"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Format Pesan Template WhatsApp Otomatis (Saat diklik)
            </label>
            <input
              type="text"
              value={contactWidget.defaultWaMessage || ''}
              onChange={(e) => setContactWidget({ ...contactWidget, defaultWaMessage: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Halo Tim Konsultan Nusantara, saya ingin bertanya dan konsultasi mengenai penawaran layanan & paket."
            />
          </div>
        </div>
      </div>

      {/* SECTION 2: Quick Stats Counter Items */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900">
              2. Kartu Angka Statistik Cepat (Quick Stats)
            </h3>
            <p className="text-xs text-slate-500">
              Ubah 4 kartu angka pencapaian perusahaan, klien, dan rating kepuasan.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickStats.items.map((stat, idx) => (
            <div key={stat.id || idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-slate-500 uppercase">
                  Kartu Statistik #{idx + 1}
                </span>
                <select
                  value={stat.icon}
                  onChange={(e) => handleUpdateStatItem(idx, 'icon', e.target.value)}
                  className="text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white font-semibold"
                >
                  <option value="Users">Ikon: Users (Klien)</option>
                  <option value="Award">Ikon: Award (Kepuasan/Prestasi)</option>
                  <option value="ShieldCheck">Ikon: ShieldCheck (Keamanan)</option>
                  <option value="HeartHandshake">Ikon: HeartHandshake (Pengalaman)</option>
                  <option value="Star">Ikon: Star (Rating)</option>
                  <option value="Zap">Ikon: Zap (Kecepatan)</option>
                  <option value="TrendingUp">Ikon: TrendingUp (Pertumbuhan)</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-0.5">
                    Angka / Nilai
                  </label>
                  <input
                    type="text"
                    value={stat.value}
                    onChange={(e) => handleUpdateStatItem(idx, 'value', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-extrabold"
                    placeholder="500+"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold text-slate-600 mb-0.5">
                    Judul Label
                  </label>
                  <input
                    type="text"
                    value={stat.label}
                    onChange={(e) => handleUpdateStatItem(idx, 'label', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold"
                    placeholder="Klien Korporasi"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-0.5">
                  Deskripsi Singkat Penjelasan
                </label>
                <input
                  type="text"
                  value={stat.desc}
                  onChange={(e) => handleUpdateStatItem(idx, 'desc', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs"
                  placeholder="Dipercaya berbagai BUMN & instansi swasta"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 3: Softselling CTA Banner */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900">
              3. Banner Penawaran & Soft-Selling (CTA)
            </h3>
            <p className="text-xs text-slate-500">
              Atur kalimat ajakan bertindak (Call To Action), garansi respon, dan teks tombol konsultasi.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Badge Penawaran Atas
            </label>
            <input
              type="text"
              value={softsellingCta.badge || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, badge: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Fast Response & Free Proposal 24 Jam"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Teks Tombol WhatsApp
            </label>
            <input
              type="text"
              value={softsellingCta.waBtnText || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, waBtnText: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Hubungi Admin WhatsApp"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Judul Utama Banner Soft-selling
            </label>
            <input
              type="text"
              value={softsellingCta.title || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, title: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-bold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Rencanakan Proyek, Portofolio & Kolaborasi Bisnis Anda"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Sub-judul / Paragraf Penjelasan
            </label>
            <textarea
              rows={2}
              value={softsellingCta.subtitle || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, subtitle: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              placeholder="Konsultasikan konsep acara, kebutuhan paket, atau digitalisasi UMKM Anda bersama tim ahli kami..."
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Keunggulan #1
            </label>
            <input
              type="text"
              value={softsellingCta.benefit1 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit1: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs"
              placeholder="Proposal & RAB dalam 24 Jam"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Keunggulan #2
            </label>
            <input
              type="text"
              value={softsellingCta.benefit2 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit2: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs"
              placeholder="Instruktur Resmi Bersertifikat BNSP"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Poin Keunggulan #3
            </label>
            <input
              type="text"
              value={softsellingCta.benefit3 || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, benefit3: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs"
              placeholder="Konsultasi 100% Bebas Biaya"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Teks Tombol Telepon
            </label>
            <input
              type="text"
              value={softsellingCta.phoneBtnText || ''}
              onChange={(e) => setSoftsellingCta({ ...softsellingCta, phoneBtnText: e.target.value })}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs"
              placeholder="Telepon Kantor Langsung"
            />
          </div>
        </div>
      </div>

      {/* Info: Footer & Contact Management Redirection */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 rounded-2xl border border-slate-700 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold shrink-0 border border-emerald-500/30">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
              <span>Pengaturan Footer & Kontak Resmi Terpusat</span>
              <span className="text-[10px] bg-emerald-500 text-slate-950 font-black px-2 py-0.5 rounded-full">
                Single Source
              </span>
            </h4>
            <p className="text-xs text-slate-300 mt-1 max-w-2xl leading-relaxed">
              Kustomisasi teks profil footer, hak cipta, navigasi bawah, dan warna footer kini dipusatkan secara lengkap dan rapi di menu <strong>"Kelola Footer & Tautan Bawah"</strong>. Sedangkan nomor telepon, WA CS, dan Google Maps dipusatkan di <strong>"Pusat Kontak & Maps"</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Action Floating Bar */}
      <div className="sticky bottom-6 z-20 flex flex-col items-end gap-1.5">
        <button
          onClick={handleSaveAll}
          disabled={isSaving}
          className="px-8 py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-extrabold text-sm shadow-xl flex items-center gap-2.5 transition-all disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Menyimpan Perubahan...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Simpan & Terapkan ke Front-End</span>
            </>
          )}
        </button>

        {lastSavedTime && (
          <div
            id="frontend-customizer-bottom-saved-notice"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-800 text-xs font-bold shadow-md animate-in fade-in slide-in-from-bottom-1"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
            <span>Berhasil disimpan ke database</span>
            <span className="text-[10px] text-emerald-700/80 font-mono font-medium">({lastSavedTime})</span>
          </div>
        )}
      </div>
    </div>
  );
}
